/**
 * @file StripeHosts.jsx
 * @description Always-mounted, tiny hosts for the Stripe payment windows. Each one only
 *   registers a handler with lib/stripeService; the payment window itself (and Stripe's
 *   SDK with it) is code-split and fetched the first time something opens it, so pages
 *   that never take a payment download none of it.
 *     - <StripeCheckoutHost/>  : openStripeCheckout({...})       -> StripeCheckoutModal
 *     - <PaymentMethodHost/>   : openPaymentMethodModal({...})   -> PaymentMethodModal
 * @module components/StripeHosts
 */
import React, { Suspense, useEffect, useState } from "react";
import { lazyWithRetry } from "../lib/lazyWithRetry";
import { registerCheckoutHost, registerPaymentMethodHost } from "../lib/stripeService";

const StripeCheckoutModal = lazyWithRetry(() => import("./StripeCheckoutModal"));
const PaymentMethodModal  = lazyWithRetry(() => import("./PaymentMethodModal"));

// The handler payload carries the caller's onComplete/onCancel; this host
// just bridges them to the modal's onClose/onComplete plumbing.
export function StripeCheckoutHost() {
  const [opts, setOpts] = useState(null);

  useEffect(() => {
    return registerCheckoutHost((newOpts) => setOpts(newOpts));
  }, []);

  if (!opts) return null;

  const close = (kind) => {
    if (kind === "cancel") {
      try { opts.onCancel?.(); } catch (_) {}
    }
    // kind === "complete": onComplete is fired by the modal BEFORE close; nothing to do here.
    setOpts(null);
  };

  return (
    <Suspense fallback={null}>
      <StripeCheckoutModal
        open
        variantKey={opts.variantKey}
        quantity={opts.quantity}
        founderTiming={opts.founderTiming}
        onError={opts.onError}
        title={opts.title}
        subtitle={opts.subtitle}
        onClose={() => close("cancel")}
        onComplete={(result) => {
          try { opts.onComplete?.(result); } catch (_) {}
          // Modal will fire its own onClose right after onComplete; don't
          // double-call close() here.
        }}
      />
    </Suspense>
  );
}

export function PaymentMethodHost() {
  const [opts, setOpts] = useState(null);

  useEffect(() => {
    return registerPaymentMethodHost((newOpts) => setOpts(newOpts));
  }, []);

  if (!opts) return null;

  const handleClose = () => {
    try { opts.onCancel?.(); } catch (_) {}
    setOpts(null);
  };

  const handleComplete = () => {
    try { opts.onComplete?.(); } catch (_) {}
    // setOpts(null) deferred — modal closes itself via timeout
  };

  return (
    <Suspense fallback={null}>
      <PaymentMethodModal open={!!opts} onClose={handleClose} onComplete={handleComplete} />
    </Suspense>
  );
}
